import { useState } from 'react'

import { useQuery } from '@apollo/client'
import { Alert, Box, Button } from '@factorialco/deprecated-design-system'
import {
  AccessForSelectFragment,
  AtsBulkCreateHiringManagersDocument,
  AtsGetAddHiringManagerModalDocument,
  AtsGetHiringTeamDocument,
} from 'generated/resources/graphql'

import { AccessSelector } from 'modules/core/components/AccessSelector'
import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  onClose: () => void
  jobId: number
}

export const AddHiringManagerModal = ({ jobId, onClose }: Props) => {
  const { data: addHiringManagerModalData } = useQuery(AtsGetAddHiringManagerModalDocument, {
    variables: {
      jobPostingId: jobId,
    },
  })

  const hiringManagersData = addHiringManagerModalData?.ats.hiringManagersConnection.nodes ?? []

  const [selectedAccessIds, setSelectedAccessIds] = useState<AccessForSelectFragment[]>([])
  const [createHiringManager, { loading }] = useMutation(AtsBulkCreateHiringManagersDocument, {
    extractorFn: (data) => data.atsMutations.bulkCreateAtsHiringManager,
  })

  const onSubmit = async () => {
    const result = await createHiringManager({
      variables: {
        accessIds: selectedAccessIds.map((access) => access.id),
        jobPostingId: jobId,
      },
      refetchQueries: [AtsGetHiringTeamDocument],
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      onClose()
    }
  }

  const onSelectAccesses = (value: AccessForSelectFragment[] | null) => {
    if (!value) {
      setSelectedAccessIds([])

      return
    }

    setSelectedAccessIds(value)
  }

  const currentHiringManagers = selectedAccessIds
    .filter((selectedAccess) =>
      hiringManagersData.some((hm) => hm.access?.id === selectedAccess.id)
    )
    .map((access) => `${access.firstName} ${access.lastName}`)
    .join(', ')

  const showWarning = selectedAccessIds.some((selectedAccess) =>
    hiringManagersData.some((hm) => hm.access?.id === selectedAccess.id)
  )

  return (
    <Modal
      illustration="user-male-female"
      title={i18n.t('ats.job_posting_settings.hiring_team.add_modal.title')}
      description={i18n.t('ats.job_posting_settings.hiring_team.add_modal.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button loading={loading} disabled={selectedAccessIds.length === 0 || showWarning} submit>
          {i18n.t('ats.job_posting_settings.hiring_team.add_modal.button')}
        </Button>
      }
    >
      <Box gap="s8">
        {showWarning && (
          <Alert color="warning">
            {i18n.t('ats.job_posting_settings.hiring_team.add_modal.already_part_of_hiring_team', {
              hiring_managers: currentHiringManagers,
            })}
          </Alert>
        )}
        <Box width="s400">
          <AccessSelector
            onSelect={(value) => onSelectAccesses(value)}
            value={selectedAccessIds}
            width="s400"
            terminated={false}
            multiple
          />
        </Box>
      </Box>
    </Modal>
  )
}
